import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../../components/BrandLogo/BrandLogo';
import { NAVY } from '../websiteTokens';
import { useWebsiteTheme } from '../useWebsiteTheme';
import ThemeToggle from '../components/ThemeToggle';

const BRAND = 'EzeitPro';
const COMPANY = 'UtilStop LLC-FZ';
const EFFECTIVE = 'May 11, 2026';
const CONTACT_EMAIL = 'sales@ezeitpro.com';
const WEBSITE = 'www.ezeitpro.com';

const tocItems = [
  { id: 'intro', label: '1. Introduction' },
  { id: 'who', label: '2. Who We Are' },
  { id: 'data-collected', label: '3. Data We Collect' },
  { id: 'use', label: '4. How We Use Your Data' },
  { id: 'legal-basis', label: '5. Legal Bases for Processing' },
  { id: 'sharing', label: '6. Sharing Your Information' },
  { id: 'transfers', label: '7. International Transfers' },
  { id: 'retention', label: '8. Data Retention' },
  { id: 'rights', label: '9. Your Rights' },
  { id: 'cookies', label: '10. Cookies & Tracking' },
  { id: 'children', label: "11. Children's Privacy" },
  { id: 'changes', label: '12. Changes to This Policy' },
  { id: 'contact', label: '13. Contact Us' },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useWebsiteTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);


  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const d = isDark ? {
    bg: NAVY.bg, surface: NAVY.surface, bgAlt: NAVY.bgAlt,
    text: NAVY.text, textSec: NAVY.textSec, textMuted: NAVY.textMuted,
    border: NAVY.border,
    primary: '#E91E63', accent: 'rgba(233,30,99,0.18)',
    badgeText: '#F48FB1',
    footBg: NAVY.footBg,
    greenBg: 'rgba(76,175,80,0.15)', greenText: '#81C784',
    blueBg: 'rgba(21,101,192,0.18)', blueText: '#64B5F6',
    yellowBg: 'rgba(255,152,0,0.12)', yellowBorder: 'rgba(255,152,0,0.3)',
    sh: '0 1px 4px rgba(3,8,20,0.6)',
    tableRow: 'rgba(255,255,255,0.03)',
  } : {
    bg: '#FAFAFA', surface: '#FFFFFF', bgAlt: '#F5F5F5',
    text: '#212121', textSec: '#424242', textMuted: '#757575',
    border: 'rgba(0,0,0,0.08)',
    primary: '#C2185B', accent: '#FCE4EC',
    badgeText: '#C2185B',
    footBg: '#212121',
    greenBg: '#E8F5E9', greenText: '#2E7D32',
    blueBg: '#E3F2FD', blueText: '#1565C0',
    yellowBg: '#FFF8E1', yellowBorder: '#FFE082',
    sh: '0 1px 4px rgba(0,0,0,0.06)',
    tableRow: 'rgba(0,0,0,0.015)',
  };

  // Helper components defined inside so they close over d and isMobile
  const S = ({ id, title, children }) => (
    <section id={id} style={{ marginBottom: '44px', scrollMarginTop: '80px' }}>
      <h2 style={{ fontSize: isMobile ? '17px' : '19px', fontWeight: 700, color: d.text, borderBottom: `2px solid ${d.accent}`, paddingBottom: '10px', marginBottom: '18px', margin: '0 0 18px' }}>{title}</h2>
      {children}
    </section>
  );

  const P = ({ children }) => (
    <p style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, lineHeight: 1.85, marginBottom: '14px', marginTop: 0 }}>{children}</p>
  );

  const Ul = ({ items }) => (
    <ul style={{ margin: '0 0 14px 0', paddingLeft: '22px' }}>
      {items.map((t, i) => (
        <li key={i} style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, lineHeight: 1.8, marginBottom: '6px' }}>{t}</li>
      ))}
    </ul>
  );

  const Tag = ({ label }) => (
    <span style={{ display: 'inline-block', background: d.accent, color: d.badgeText, fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', marginRight: '6px', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
  );

  const ContactCard = ({ items }) => (
    <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {items.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: '16px', padding: '10px 0', borderBottom: i < items.length - 1 ? `1px solid ${d.border}` : 'none', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
          <span style={{ fontSize: '12px', fontWeight: 600, color: d.textMuted, minWidth: '70px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{c.label}</span>
          {c.href
            ? <a href={c.href} style={{ fontSize: '14px', color: d.primary, textDecoration: 'none', fontWeight: 500 }}>{c.value}</a>
            : <span style={{ fontSize: '14px', color: d.text }}>{c.value}</span>
          }
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Roboto','Segoe UI',Arial,sans-serif", background: d.bg, color: d.text, minHeight: '100vh', transition: 'background 0.3s,color 0.3s', overflowX: 'hidden' }}>

      {/* Header */}
      <header style={{ background: d.surface, borderBottom: `1px solid ${d.border}`, padding: `0 ${isMobile ? '16px' : '32px'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', position: 'sticky', top: 0, zIndex: 100, boxShadow: d.sh }}>
        <BrandLogo height={36} tone={isDark ? 'dark' : 'light'} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} color={d.text} border={d.border} size={36} />
          <button
            onClick={() => navigate('/')}
            style={{ background: d.bgAlt, border: `1px solid ${d.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: d.textSec, fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Banner */}
      <div style={{ background: isDark ? `linear-gradient(135deg,#1a0a2e 0%,#16213e 100%)` : `linear-gradient(135deg,${d.accent} 0%,#f5f5f5 100%)`, padding: isMobile ? '36px 20px 30px' : '48px 32px 40px', textAlign: 'center', borderBottom: `1px solid ${d.border}` }}>
        <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Legal Document</div>
        <h1 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 700, color: d.text, margin: '0 0 10px' }}>Privacy Policy</h1>
        <p style={{ fontSize: '15px', color: d.textMuted, margin: 0 }}>Effective Date: <strong style={{ color: d.textSec }}>{EFFECTIVE}</strong> &nbsp;·&nbsp; {COMPANY}</p>
      </div>

      {/* Body grid */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '28px 16px 60px' : '48px 24px 80px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 1fr', gap: isMobile ? '0' : '48px', alignItems: 'start' }}>

        {/* TOC sidebar — hidden on mobile */}
        {!isMobile && (
          <aside style={{ position: 'sticky', top: '80px' }}>
            <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '20px', boxShadow: d.sh }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: d.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' }}>Contents</div>
              {tocItems.map(item => (
                <a key={item.id} href={`#${item.id}`}
                  style={{ display: 'block', fontSize: '13px', color: d.textSec, textDecoration: 'none', padding: '5px 0', borderBottom: `1px solid ${d.border}`, lineHeight: 1.4, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = d.primary}
                  onMouseLeave={e => e.currentTarget.style.color = d.textSec}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </aside>
        )}

        {/* Mobile TOC — compact pill list */}
        {isMobile && (
          <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '16px', marginBottom: '24px', boxShadow: d.sh }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: d.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>Jump to section</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {tocItems.map(item => (
                <a key={item.id} href={`#${item.id}`}
                  style={{ fontSize: '12px', color: d.primary, background: d.accent, padding: '4px 10px', borderRadius: '20px', textDecoration: 'none', fontWeight: 500 }}
                >
                  {item.label.split('. ')[0]}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Main content */}
        <main>

          <S id="intro" title="1. Introduction">
            <P>{BRAND} ("{BRAND}", "we", "our", or "us"), operated by {COMPANY}, a free zone limited liability company incorporated under the laws of the United Arab Emirates, is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, store, and share information when you use our SaaS platform, website ({WEBSITE}), and related services (collectively, the "Services").</P>
            <P>This Policy applies to all users of {BRAND}, including service business operators (vendors), their staff members, and end customers who interact with our platform. Please read this Policy carefully before using our Services. By accessing or using {BRAND}, you acknowledge that you have read, understood, and agree to this Privacy Policy.</P>
          </S>

          <S id="who" title="2. Who We Are">
            <P><strong>Data Controller:</strong> {COMPANY}, registered in Dubai, United Arab Emirates.</P>
            <P><strong>Platform:</strong> {BRAND} — an all-in-one SaaS platform providing scheduling, booking, crew management, invoicing, analytics, and customer communications for service businesses.</P>
            <P><strong>Contact:</strong> For privacy-related enquiries, please contact us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: d.primary }}>{CONTACT_EMAIL}</a>.</P>
            <P>Where we process personal data of individuals in the European Economic Area (EEA) or United Kingdom, {COMPANY} acts as a data controller. Vendors using our platform to manage their own customers act as independent data controllers for that customer data; {BRAND} acts as a data processor on their behalf.</P>
          </S>

          <S id="data-collected" title="3. Data We Collect">
            <P>We collect the following categories of personal data:</P>
            <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '8px' }}>3.1 Information You Provide Directly</h3>
            <Ul items={[
              'Identity data: full name, job title, business name',
              'Contact data: email address, phone number (including WhatsApp number), business address, billing address',
              'Account credentials: username, hashed passwords',
              'Business profile: service category, operating hours, team structure, service offerings',
              'Payment information: billing details, VAT/GST registration numbers (payment card data is processed by Stripe and never stored on our servers)',
              'Booking and service data: appointment details, customer notes, service history, invoices',
              'Communications: messages sent through our in-platform messaging and WhatsApp integration',
              'Demo and enquiry data: information submitted via our demo request form',
            ]} />
            <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '8px' }}>3.2 Information Collected Automatically</h3>
            <Ul items={[
              'Device and technical data: IP address, browser type and version, operating system, device identifiers',
              'Usage data: pages visited, features used, session duration, click patterns, error logs',
              'Location data: approximate geographic location derived from IP address; precise location only if you grant permission',
              'Cookies and similar tracking technologies (see Section 10)',
            ]} />
            <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '8px' }}>3.3 Data from Third Parties</h3>
            <Ul items={[
              'Stripe: transaction confirmation, payment status, and fraud signals (we do not receive full card numbers)',
              'WhatsApp Business API: message delivery status and customer interactions',
              'Google Business Profile integration: booking requests and business listing data',
              'Analytics providers: aggregated usage and performance data',
            ]} />
          </S>

          <S id="use" title="4. How We Use Your Data">
            <P>We use your personal data for the following purposes:</P>
            <Ul items={[
              'Providing and operating the Services: account creation, booking management, scheduling, invoicing, crew management, and analytics',
              'Processing payments: billing your subscription, generating tax-compliant invoices (UAE VAT, India GST)',
              'Customer communications: sending booking confirmations, reminders, follow-ups via SMS, email, and WhatsApp',
              'Platform security: detecting fraud, abuse, and unauthorised access; enforcing our Terms of Service',
              'Product improvement: analysing usage patterns to improve features and user experience (using anonymised and aggregated data where possible)',
              'Customer support: responding to your queries, troubleshooting, and resolving disputes',
              'Legal compliance: meeting our obligations under UAE law, India law, and GDPR (where applicable)',
              'Marketing communications: sending product updates and promotional offers to existing customers (you may opt out at any time)',
            ]} />
          </S>

          <S id="legal-basis" title="5. Legal Bases for Processing (GDPR)">
            <P>For users in the European Economic Area (EEA) or United Kingdom, we process personal data on the following legal grounds under the GDPR:</P>
            <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '10px', overflow: 'hidden', marginBottom: '14px' }}>
              {[
                { basis: 'Contract Performance', desc: 'Processing your data to provide the Services you have contracted for, including account management, booking operations, and invoicing.' },
                { basis: 'Legitimate Interests', desc: 'Improving our platform, preventing fraud, ensuring security, and sending service-related communications where our interests do not override your rights.' },
                { basis: 'Legal Obligation', desc: 'Complying with applicable laws, including tax obligations, financial record-keeping, and responding to lawful authority requests.' },
                { basis: 'Consent', desc: 'Marketing communications and non-essential cookies. You may withdraw consent at any time without affecting prior lawful processing.' },
              ].map((row, i) => (
                <div key={i} style={{ display: isMobile ? 'flex' : 'grid', flexDirection: isMobile ? 'column' : undefined, gridTemplateColumns: isMobile ? undefined : '170px 1fr', borderBottom: i < 3 ? `1px solid ${d.border}` : 'none' }}>
                  <div style={{ padding: '12px 16px', background: d.bgAlt, fontWeight: 600, fontSize: '13px', color: d.primary, borderRight: isMobile ? 'none' : `1px solid ${d.border}`, borderBottom: isMobile ? `1px solid ${d.border}` : 'none' }}>{row.basis}</div>
                  <div style={{ padding: '12px 16px', fontSize: '13px', color: d.textSec, lineHeight: 1.7 }}>{row.desc}</div>
                </div>
              ))}
            </div>
            <P>For users in the UAE, processing is conducted in accordance with UAE Federal Decree-Law No. 45 of 2021 on the Protection of Personal Data (UAE PDPL) and its implementing regulations. For users in India, processing is conducted in accordance with the Digital Personal Data Protection Act 2023 (India DPDPA).</P>
          </S>

          <S id="sharing" title="6. Sharing Your Information">
            <P>We do not sell your personal data. We share data only in the following limited circumstances:</P>
            <Ul items={[
              'Service providers: Stripe (payment processing), AWS / cloud hosting providers, communication providers (SMS, WhatsApp Business API), and analytics platforms — all bound by data processing agreements',
              'Business transfers: in the event of a merger, acquisition, or sale of assets, your data may be transferred to a successor entity, subject to the same privacy protections',
              'Legal compliance: when required by law, court order, or regulatory authority in the UAE, India, or the EEA',
              'Fraud prevention: to detect, prevent, or address fraud, security breaches, or technical issues',
              'With your consent: for any other purpose with your explicit prior consent',
            ]} />
            <P>We require all third-party service providers to implement appropriate security measures and to process personal data only for specified, documented purposes.</P>
          </S>

          <S id="transfers" title="7. International Data Transfers">
            <P>{BRAND} is operated from the United Arab Emirates and serves customers in the UAE, India, and other jurisdictions. Your data may be transferred to and processed in countries outside your own.</P>
            <div style={{ background: isDark ? 'rgba(233,30,99,0.08)' : '#fff5f5', border: `1px solid ${d.accent}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '14px' }}>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <Tag label="GDPR Art. 46" /><Tag label="UAE PDPL" /><Tag label="India DPDPA" />
              </div>
              <p style={{ fontSize: '14px', color: d.textSec, lineHeight: 1.75, margin: 0 }}>
                For transfers from the EEA or UK, we rely on Standard Contractual Clauses (SCCs) approved by the European Commission, or other appropriate safeguards under GDPR Article 46. For cross-border transfers under UAE PDPL, we implement contractual protections and ensure recipient countries provide adequate protection. For cross-border transfers under India DPDPA, we comply with applicable Central Government restrictions and safeguards.
              </p>
            </div>
            <P>Our primary cloud infrastructure is hosted in AWS data centres, with data processed in regions that offer appropriate data protection standards.</P>
          </S>

          <S id="retention" title="8. Data Retention">
            <P>We retain personal data for as long as necessary to fulfil the purposes described in this Policy, unless a longer retention period is required by law. Our general retention schedules are:</P>
            <Ul items={[
              'Account data: retained for the duration of your subscription and for 3 years after account closure, or as required by applicable tax and financial laws',
              'Transaction and invoice records: 7 years (UAE VAT compliance) / 8 years (India GST compliance)',
              'Booking and service records: 2 years from the date of service',
              'Communications logs: 12 months from creation',
              'Security and access logs: 12 months',
              'Marketing consent records: until you withdraw consent plus 3 years',
            ]} />
            <P>After expiry of the applicable retention period, we securely delete or anonymise your data. Data in backups is deleted on the next scheduled purge cycle following the retention period expiry.</P>
          </S>

          <S id="rights" title="9. Your Rights">
            <P>Depending on your jurisdiction, you have the following rights regarding your personal data:</P>
            <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '10px' }}>Under GDPR (EEA / UK users)</h3>
            <Ul items={[
              'Right of access: obtain a copy of the personal data we hold about you',
              'Right to rectification: correct inaccurate or incomplete data',
              'Right to erasure ("right to be forgotten"): request deletion of your data where no legitimate legal basis for continued processing exists',
              'Right to restriction of processing: limit how we use your data in certain circumstances',
              'Right to data portability: receive your data in a machine-readable format',
              'Right to object: object to processing based on legitimate interests or for direct marketing',
              'Rights related to automated decision-making: not be subject to solely automated decisions that significantly affect you',
              'Right to withdraw consent: at any time, without affecting the lawfulness of prior processing',
            ]} />
            <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '10px' }}>Under UAE PDPL (UAE users)</h3>
            <Ul items={[
              'Right to be informed about the data we collect and how we use it',
              'Right to access your personal data',
              'Right to correct inaccurate data',
              'Right to erasure in circumstances permitted by UAE law',
              'Right to withdraw consent for consent-based processing',
            ]} />
            <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '10px' }}>Under India DPDPA (India users)</h3>
            <Ul items={[
              'Right to access information about personal data processed and processing activities',
              'Right to correction and erasure of inaccurate or outdated personal data',
              'Right to grievance redressal in respect of the exercise of your rights',
              'Right to nominate another individual to exercise rights on your behalf',
            ]} />
            <P>To exercise any of these rights, please contact us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: d.primary }}>{CONTACT_EMAIL}</a>. We will respond within 30 days. You also have the right to lodge a complaint with your local data protection authority.</P>
          </S>

          <S id="cookies" title="10. Cookies & Tracking Technologies">
            <P>We use cookies and similar technologies to ensure the platform functions correctly, understand usage, and improve your experience. Categories of cookies we use:</P>
            <Ul items={[
              'Strictly necessary cookies: required for authentication, session management, and core platform functionality. These cannot be disabled.',
              'Performance cookies: collect anonymised usage analytics (e.g., page views, error rates) to help us improve the platform.',
              'Functional cookies: remember your preferences (e.g., language, dashboard settings).',
              'Marketing cookies (website only): used with your consent to understand which of our marketing channels bring users to the website.',
            ]} />
            <P>You may manage non-essential cookie preferences through your browser settings. Please note that disabling certain cookies may affect platform functionality.</P>
          </S>

          <S id="children" title="11. Children's Privacy">
            <P>{BRAND} is designed exclusively for business users and is not directed at or intended for use by individuals under the age of 18. We do not knowingly collect personal data from children. If we become aware that a child under 18 has provided us with personal data, we will take steps to delete it promptly. If you believe we have inadvertently collected information from a minor, please contact us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: d.primary }}>{CONTACT_EMAIL}</a>.</P>
          </S>

          <S id="changes" title="12. Changes to This Policy">
            <P>We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or platform capabilities. When we make material changes, we will notify you by email and by posting a prominent notice on our platform at least 30 days before the changes take effect. The "Effective Date" at the top of this page will always indicate when the Policy was last updated.</P>
            <P>Your continued use of {BRAND} after the effective date of any updated Policy constitutes your acceptance of the changes.</P>
          </S>

          <S id="contact" title="13. Contact Us">
            <P>If you have any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact our privacy team:</P>
            <ContactCard items={[
              { label: 'Company', value: COMPANY },
              { label: 'Platform', value: BRAND },
              { label: 'Email', value: CONTACT_EMAIL, href: `mailto:${CONTACT_EMAIL}` },
              { label: 'Website', value: WEBSITE, href: `https://${WEBSITE}` },
              { label: 'Address', value: 'Dubai, United Arab Emirates' },
            ]} />
          </S>

        </main>
      </div>

      {/* Footer */}
      <footer style={{ background: d.footBg, color: '#9E9E9E', padding: '24px 32px', textAlign: 'center', fontSize: '13px' }}>
        <p style={{ margin: 0 }}>
          © 2026 {COMPANY}, Dubai. All rights reserved. &nbsp;·&nbsp;
          <a href="/privacy-policy" style={{ color: d.primary, textDecoration: 'none' }}>Privacy</a> &nbsp;·&nbsp;
          <a href="/terms" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Terms</a> &nbsp;·&nbsp;
          <a href="/security" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Security</a>
        </p>
      </footer>
    </div>
  );
}
